import * as Sentry from '@sentry/browser';
import { LitElement } from 'lit';
import { createContext, ContextProvider } from '@lit/context';
import { useAuth } from '../firebase/auth.js';

// Create auth context
export const authContext = createContext('auth');

/**
 * Auth Context Provider Component
 * Wraps the app to provide authentication state to all child components
 */
export class AuthContextProvider extends LitElement {
    // Use Light DOM (no Shadow DOM)
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();

        // Initialize auth state
        const initialState = {
            user: null,
            authenticated: false,
            loading: true,
            error: null,
        };

        // Create context provider using functional API
        this._provider = new ContextProvider(this, {
            context: authContext,
            initialValue: initialState,
        });

        // Setup auth listener
        this._setupAuthListener();
    }

    _setupAuthListener() {
        const { setupAuthListener } = useAuth();

        // Setup listener with callback to update state
        this._unsubscribe = setupAuthListener((newState) => {
            // Set/clear Sentry user context based on auth state
            if (newState.authenticated && newState.user) {
                Sentry.setUser({
                    id: newState.user.uid,
                    email: newState.user.email,
                });
            } else if (!newState.authenticated) {
                // Clear user context on sign-out
                Sentry.setUser(null);
                Sentry.setContext('quote', null);
            }

            // Update the context value
            this._provider.setValue({ ...newState });

            // Dispatch custom event for non-component listeners (like router)
            window.dispatchEvent(
                new CustomEvent('auth-state-changed', {
                    detail: newState,
                })
            );
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        // Cleanup listener on component unmount
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    render() {
        // Just render children (slot)
        return this.children;
    }
}

// Define custom element
customElements.define('auth-context-provider', AuthContextProvider);
